/**
 * LIVE-SWITCHES-CARD (review 2026-11-02 §6; Seth: "If there is a setting that
 * needs permissions that should be wired up and easy. Everything should be
 * editable and changeable and not just something you have to do.")
 *
 * The four master gates lived only in SQL (`org_setting` rows flipped by hand):
 *   comms_live              — customer texts + emails leave the building
 *   payments_live           — real cards are charged / refunded
 *   team_invites_live       — invite + password-reset emails go out
 *   dispatch_numbering_live — Pegasus continues ESC's dispatch numbers (one-way)
 * Now they are one card under Settings → System. Admins flip them (the table's
 * RLS already allows admin writes only); everyone with `settings.manage` can
 * SEE the state, which is the point — "is comms live?" should never need SQL.
 * Every flip is a normal `org_setting` upsert, so the zzaudit trigger records
 * it with who and when, and the row shows that stamp.
 *
 * Competitor frame: ServiceTitan has no switchboard — go-live is a per-module
 * tangle of settings plus a sandbox account; HubSpot shows each channel as a
 * "Connected / Not connected" toggle on one page; Jobber gates client
 * notifications per feature. Adopted HubSpot's one-page, one-toggle-per-channel
 * shape; rejected ServiceTitan's scatter. Added what none of them have: a
 * confirm step on every flip (these are the only four switches in Pegasus that
 * touch a customer's phone, card or inbox) and the audit stamp on the row.
 *
 * DESIGN-CALM-01 / DESIGN-QUIET-02: labels are nouns, the help sits under the
 * (i). The switch control is local to this file until the kit grows a Switch
 * (DESIGN-SYSTEM-02); it is the shape the kit one will take.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Hash, Lock, MailPlus, MessageSquareText, type LucideIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { Button } from "@/components/ui/Button";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { useSaveOrgSetting } from "@/lib/orgSettings";

interface SwitchDef {
  key: "comms_live" | "payments_live" | "team_invites_live" | "dispatch_numbering_live";
  label: string;
  icon: LucideIcon;
  /** Once on, stays on (the switch locks). */
  oneWay?: boolean;
  tip: string;
}

const SWITCHES: SwitchDef[] = [
  {
    key: "comms_live",
    label: "Customer Messages",
    icon: MessageSquareText,
    tip: "On: texts and emails to customers really go out — automations, reminders, proposal and invoice sends, and Podium replies. Off: everything lands in the Outbox as Held, nothing reaches a customer, and the office still sees what would have gone.",
  },
  {
    key: "payments_live",
    label: "Card Payments",
    icon: CreditCard,
    tip: "On: Collect Payment and the customer portal charge and refund real cards through Stripe, and autopay runs for plans. Off: card buttons stay dark, autopay skips, and only hand-recorded payments (cash, check, a card run elsewhere) can be entered.",
  },
  {
    key: "team_invites_live",
    label: "Login Invites",
    icon: MailPlus,
    tip: "On: creating a login emails a set-your-password invite, and Resend / Send Reset email the person. Off: no invite or reset email goes out; an admin hands over a temporary password in person instead.",
  },
  {
    key: "dispatch_numbering_live",
    label: "Dispatch Numbers",
    icon: Hash,
    oneWay: true,
    tip: "On: every call booked in Pegasus takes the next number in ESC's dispatch sequence, so numbers stay one unbroken run across both systems. This was flipped at go-live and stays on — turning it off would leave new calls without a number.",
  },
];

interface Row {
  key: string;
  value: unknown;
  updated_at: string | null;
  updated_by: string | null;
}

function useLiveSwitches() {
  return useQuery({
    queryKey: ["live_switches"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<Map<string, Row>> => {
      const { data, error } = await supabase
        .from("org_setting")
        .select("key, value, updated_at, updated_by")
        .in(
          "key",
          SWITCHES.map((s) => s.key),
        );
      if (error) throw error;
      return new Map(((data ?? []) as Row[]).map((r) => [r.key, r]));
    },
  });
}

function isOn(v: unknown): boolean {
  return v === true || v === "true";
}

function whenText(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/Chicago" });
}

/** The toggle itself — role="switch", keyboard + screen-reader friendly. */
function Toggle({ on, disabled, label, onClick }: { on: boolean; disabled?: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
        on ? "bg-brand-600" : "bg-slate-300",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "inline-block h-5 w-5 transform rounded-full bg-white shadow-e1 transition-transform",
          on ? "translate-x-[22px]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

export function LiveSwitchesSection() {
  const { isAdmin } = usePermissions();
  const rows = useLiveSwitches();
  const save = useSaveOrgSetting();
  const qc = useQueryClient();
  const [arming, setArming] = useState<SwitchDef["key"] | null>(null);

  async function flip(def: SwitchDef, next: boolean) {
    await save.mutateAsync({ key: def.key, value: next });
    setArming(null);
    void qc.invalidateQueries({ queryKey: ["live_switches"] });
    // Readers that keep their own key (the comms banner, Outbox counts).
    void qc.invalidateQueries({ queryKey: ["comms_live"] });
    void qc.invalidateQueries({ queryKey: ["org_setting", def.key] });
  }

  return (
    <div className="rounded-xl border border-hairline bg-white shadow-e1">
      <ul className="divide-y divide-hairline">
        {SWITCHES.map((def) => {
          const row = rows.data?.get(def.key);
          const on = isOn(row?.value);
          const locked = Boolean(def.oneWay && on);
          const canFlip = isAdmin && !locked && !rows.isLoading;
          const Icon = def.icon;
          const isArming = arming === def.key;
          const next = !on;
          return (
            <li key={def.key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600">
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-slate-900">{def.label}</span>
                  <InfoTip label={`About ${def.label}`}>{def.tip}</InfoTip>
                </div>
                {row?.updated_at && (
                  <div className="mt-0.5 truncate text-[12px] text-slate-500">
                    Since {whenText(row.updated_at)}
                    {row.updated_by ? ` · ${row.updated_by}` : ""}
                  </div>
                )}
              </div>

              {isArming ? (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant={next ? "primary" : "danger"} loading={save.isPending} onClick={() => void flip(def, next)}>
                    {next ? "Turn On" : "Turn Off"}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => setArming(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold",
                      on ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600",
                    )}
                  >
                    {locked && <Lock className="h-3 w-3" strokeWidth={2} />}
                    {rows.isLoading ? "…" : on ? "On" : "Off"}
                  </span>
                  <Toggle on={on} disabled={!canFlip} label={`${def.label} ${on ? "on" : "off"}`} onClick={() => setArming(def.key)} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
